import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { DEV } from 'esm-env';
import { ReactNode } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { TssCacheProvider } from 'tss-react';

const ltrPlugins = DEV ? [] : [prefixer];
const rtlPlugins = [rtlPlugin].concat(ltrPlugins);

const cacheMap = new Map<string, ReturnType<typeof createCache>>();

const buildCache = (direction: 'ltr' | 'rtl', scope: string, speedy?: boolean) => {
  const key = `${scope}-${direction}`;
  if (cacheMap.has(key)) {
    return cacheMap.get(key)!;
  }
  const cache = createCache({
    key,
    prepend: scope === 'mui',
    stylisPlugins: direction === 'rtl' ? rtlPlugins : ltrPlugins,
    speedy: speedy ?? false,
  });
  cacheMap.set(key, cache);
  return cache;
};

type Props = {
  theme: Theme;
  disableProductionOptimization?: boolean;
  children: ReactNode;
};

const MuiThemeProvider = ({ theme, disableProductionOptimization = DEV, children }: Props) => (
  <CacheProvider value={buildCache(theme.direction, 'mui', !disableProductionOptimization)}>
    <TssCacheProvider value={buildCache(theme.direction, 'tss', !disableProductionOptimization)}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </TssCacheProvider>
  </CacheProvider>
);

export default MuiThemeProvider;
